import { useMutation, useQueryClient } from "react-query";
import {
  sendOrderReceiptToPrint,
  sendReceipt,
  updateOrderDate,
  updateOrderCustomer,
  updateOrderStatus,
  assignRiderToOrder,
  createOrderV2,
  continuePendingSession,
  cancelPendingSession,
  createPendCheckoutSession,
  addToCartBulk,
  createBillOrderV2,
  createEndOfDayDetailsUrl,
  updateOrderPaymentMethod,
  createOrderAndPendPayment,
  printReturnsReceipt,
  createPharmacyIndication,
  createPharmacyIntervention,
  updateOrderTable,
  updateOrderNote,
} from "../../../services/sales-point";
import {
  acceptOrRejectStorefrontOrder,
  payViaStoreWalletMutation,
  reverseOrder,
  updateOrderStatusConfirmation,
  updateReturnDate,
} from "../../../services/inventory";
import { useContext } from "react";
import { RequestLoader, ToastContext } from "../../context";
import { getRandomIntInclusive } from "../../../helpers";

export const useSendOrderReceiptMutation = () => {
  return useMutation((data) => sendReceipt(data));
};

export const useSendOrderReceiptToPrint = () => {
  return useMutation((data) => sendOrderReceiptToPrint(data));
};

export const useCancelOrderMutation = () => {
  return useMutation((data) => reverseOrder(data));
};

export const useUpdateOrderDate = ({ closeModal }) => {
  const updateOrder = useMutation((data) => updateOrderDate(data));
  const triggerToast = useContext(ToastContext);
  const queryClient = useQueryClient();

  const handleUpdateOrderDate = (data) => {
    updateOrder.mutate(data, {
      onSuccess: () => {
        triggerToast("Order date update successfully", "success");
        queryClient.invalidateQueries(["orderDetails"]);
        queryClient.invalidateQueries(["getOrderList"]);
        closeModal();
      },
    });
  };
  return {
    handleUpdateOrderDate,
    isLoading: updateOrder.isLoading,
  };
};

export const useUpdateReturnDate = () => {
  return useMutation((data) => updateReturnDate(data));
};

export const useUpdateOrderCustomer = ({ closeWidget }) => {
  const updateOrder = useMutation((data) => updateOrderCustomer(data));
  const triggerToast = useContext(ToastContext);
  const queryClient = useQueryClient();

  const handleUpdateOrderCustomer = (data) => {
    updateOrder.mutate(data, {
      onSuccess: () => {
        triggerToast("Order customer update successfully", "success");
        queryClient.invalidateQueries([
          "orderDetails",
          `${data?.data?.order_id}`,
        ]);
        queryClient.invalidateQueries(["getOrderList"]);
        closeWidget();
      },
    });
  };
  return {
    handleUpdateOrderCustomer,
    isLoading: updateOrder.isLoading,
  };
};

export const useUpdateOrderStatusMutation = ({ onSuccess = () => {} }) => {
  const updateOrderStatusMutate = useMutation((data) =>
    updateOrderStatus(data),
  );
  const triggerToast = useContext(ToastContext);
  const queryClient = useQueryClient();

  const updateStatus = (orderId, value) => {
    updateOrderStatusMutate.mutate(
      { id: orderId, data: { status: value } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["getOrderList"]);
          queryClient.invalidateQueries(["orderDetails", `${orderId}`]);
          onSuccess();
          triggerToast("Order status updated successfully", "success");
        },
      },
    );
  };

  return { ...updateOrderStatus, updateStatus };
};

export const useUpdateOrderNote = () => {
  return useMutation((data) => updateOrderNote(data));
};

export const useAssignRiderToOrderMutation = ({ onSuccess = () => {} }) => {
  const assignRiderToOrderMutate = useMutation((data) =>
    assignRiderToOrder(data),
  );
  const triggerToast = useContext(ToastContext);
  const queryClient = useQueryClient();

  const assignRider = (orderId, value) => {
    assignRiderToOrderMutate.mutate(
      { order_id: orderId, dispatch_rider_id: value },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["getOrderList"]);
          queryClient.invalidateQueries(["orderDetails", `${orderId}`]);
          onSuccess();
          triggerToast(
            "Order assigned and sent to rider successfully",
            "success",
          );
        },
      },
    );
  };

  return { ...assignRiderToOrder, assignRider };
};

export const useAcceptOrRejectOrdereMutation = () => {
  return useMutation((data) => acceptOrRejectStorefrontOrder(data));
};

export const useUpdateOrderStatusConfirmation = () => {
  return useMutation((data) => updateOrderStatusConfirmation(data));
};

export const usePayViaStoreWalletMutation = () => {
  return useMutation((data) => payViaStoreWalletMutation(data));
};

export const useCreateOrderMutation = () => {
  return useMutation((data) => createOrderV2(data));
};

export const useGenerateBillMutation = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => createOrderV2(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getBillOrders"]);
    },
  });
};

export const useCompleteGenerateBill = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => createBillOrderV2(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getBillOrders"]);
    },
  });
};

export const useCreateOrderAndPendPayment = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => createOrderAndPendPayment(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["getBillOrders"]);
    },
  });
};

export const useContinuePendingSession = () => {
  return useMutation((data) => continuePendingSession(data));
};

export const useCancelPendingSession = () => {
  return useMutation((data) => cancelPendingSession(data));
};

export const useCreatePendCheckoutSession = () => {
  return useMutation((data) => createPendCheckoutSession(data));
};

export const useBulkAddItemsToCart = () => {
  return useMutation((data) => addToCartBulk(data));
};

export const useReverseOrder = () => {
  const queryClient = useQueryClient();
  const triggerToast = useContext(ToastContext);
  const { setRequestLoaderProgress } = useContext(RequestLoader);

  const reverseOrderMutation = useMutation((data) => reverseOrder(data));

  const cancelOrder = ({ data, onSuccess = () => {} }) => {
    setRequestLoaderProgress(getRandomIntInclusive(10, 40));

    reverseOrderMutation.mutate(data, {
      onSuccess: () => {
        triggerToast("Order Cancelled Successfully", "success");
        queryClient.invalidateQueries(["orderDetails", `${data?.orderId}`]);
        queryClient.invalidateQueries(["getOrderList"]);
        onSuccess();
      },
      onSettled: () => setRequestLoaderProgress(100),
    });
  };

  return { cancelOrder, ...reverseOrderMutation };
};

export const useCreateEndOfDayDetailsUrl = () => {
  return useMutation((data) => createEndOfDayDetailsUrl(data));
};

export const useUpdateOrderPaymentMethodMutation = ({
  onSuccess = () => {},
}) => {
  const updateOrderPaymentMethodMutation = useMutation((data) =>
    updateOrderPaymentMethod(data),
  );
  const triggerToast = useContext(ToastContext);
  const queryClient = useQueryClient();

  const updatePaymentMethod = ({ orderId, value }) => {
    updateOrderPaymentMethodMutation.mutate(
      {
        id: orderId,
        data: {
          payment_method: value,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["getOrderList"]);
          queryClient.invalidateQueries(["orderDetails", `${orderId}`]);
          onSuccess();
          triggerToast("Order payment method updated successfully", "success");
        },
      },
    );
  };

  return { ...updateOrderPaymentMethod, updatePaymentMethod };
};

export const usePrintReturnsReceipt = () => {
  return useMutation((data) => printReturnsReceipt(data));
};

export const useCreatePharmacyIndication = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => createPharmacyIndication(data), {
    onSuccess: () => {
      queryClient.invalidateQueries("getPharmacyOption");
    },
  });
};

export const useCreatePharmacyIntervention = () => {
  const queryClient = useQueryClient();
  return useMutation((data) => createPharmacyIntervention(data), {
    onSuccess: () => {
      queryClient.invalidateQueries("getPharmacyOption");
    },
  });
};

export const useUpdateOrderTable = ({ closeModal }) => {
  const updateOrder = useMutation((data) => updateOrderTable(data));
  const triggerToast = useContext(ToastContext);
  const queryClient = useQueryClient();

  const handleUpdateOrderTable = (data) => {
    updateOrder.mutate(data, {
      onSuccess: () => {
        triggerToast("Order table updated successfully", "success");
        queryClient.invalidateQueries(["orderDetails"]);
        queryClient.invalidateQueries(["getOrderList"]);
        closeModal();
      },
    });
  };
  return {
    handleUpdateOrderTable,
    isLoading: updateOrder.isLoading,
  };
};
